import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    Input,
    OnInit,
    Self,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    FormsModule,
    NgControl,
    NonNullableFormBuilder,
    ReactiveFormsModule,
} from '@angular/forms';
import {TranslatePipe} from '@brunata-angular/util';
import {v4 as uuidv4} from 'uuid';
import {ControlMessagesComponent} from '../control-messages/control-messages.component';
import {CommonModule} from '@angular/common';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

export type CheckBoxControl = FormGroup<{
    name: FormControl<string>;
    selected: FormControl<boolean>;
    disabled?: FormControl<boolean>;
    hint?: FormControl<string | null>;
}>;

export type SelectableItem = {
    name: string;
    selected: boolean;
    sortOrder?: number;
    disabled?: boolean;
    hint?: string | null;
};

export type SelectableItemType<T> = T & {selected: boolean};

export function createCheckBoxControl(item: SelectableItem): CheckBoxControl {
    const selected = new FormControl<boolean>(item.selected, {nonNullable: true});
    if (item.disabled === true) {
        selected.disable({emitEvent: false});
    }
    return new FormGroup<CheckBoxControl['controls']>({
        name: new FormControl<string>(item.name, {nonNullable: true}),
        selected,
        disabled: new FormControl<boolean>(item.disabled === true, {nonNullable: true}),
        hint: new FormControl<string | null>(item.hint ?? null),
    });
}

export function reapplyItemDisabledState(checkBoxControls: CheckBoxControl[]): void {
    checkBoxControls
        .filter(item => item.controls.disabled?.value === true)
        .forEach(item => item.controls.selected.disable({emitEvent: false}));
}

@Component({
    selector: 'app-input-checkboxes',
    templateUrl: './input-checkboxes.component.html',
    styleUrls: ['./input-checkboxes.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        ControlMessagesComponent,
        TranslatePipe,
    ],
})
export class InputCheckboxesComponent implements ControlValueAccessor, OnInit {
    @Input() label = '';
    @Input() id: string = uuidv4();
    @Input() required = false;

    formGroup = this.formBuilder.group({
        formArray: this.formBuilder.array<CheckBoxControl>([]),
    });

    private onTouch?: () => void;
    private onChange?: (value: SelectableItem[]) => void;

    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
        private formBuilder: NonNullableFormBuilder,
        private destroyRef: DestroyRef,
    ) {
        ngControl.valueAccessor = this;
    }

    ngOnInit(): void {
        this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this.onChange == null) {
                return;
            }

            this.changeDetectorRef.markForCheck();
            this.onChange(this.formGroup.controls.formArray.getRawValue());
        });

        if (this.ngControl.control) {
            const originalMarkAsTouched = this.ngControl.control.markAsTouched.bind(
                this.ngControl.control,
            );
            this.ngControl.control.markAsTouched = (...args): void => {
                originalMarkAsTouched(...args);
                this.changeDetectorRef.markForCheck();
            };
        }
    }

    registerOnChange(fn: (value: SelectableItem[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(values: SelectableItem[]): void {
        this.formGroup.controls.formArray.clear();

        values.forEach(item => {
            this.formGroup.controls.formArray.push(createCheckBoxControl(item));
        });
    }

    setDisabledState(disabled: boolean): void {
        if (disabled) {
            this.formGroup.disable();
            return;
        }
        this.formGroup.enable();
        reapplyItemDisabledState(this.formGroup.controls.formArray.controls);
    }
}
